import React, { useState } from 'react';
import Logo from '../components/Logo';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import WhatsAppButton from '../components/WhatsAppButton';
import SpecializationFilter from '../components/SpecializationFilter';
import { partners } from '../data/partners';
import {
    Shield,
    Heart,
    Scale,
    Home as HomeIcon,
    ArrowRight,
    Users,
    Award,
    Phone,
    Mail,
    MapPin,
    Send,
    Compass,
    CheckCircle2,
    ShieldCheck
} from 'lucide-react';

const Home = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        specialization: '',
        message: '',
    });

    const [formSubmitted, setFormSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        setTimeout(() => {
            alert("Grazie per la richiesta! Mauro Astore la ricontatterà direttamente entro 24 ore lavorative.");
            setFormData({ name: '', email: '', phone: '', specialization: '', message: '' });
            setFormSubmitted(false);
        }, 500);
    };

    const pillars = [
        {
            icon: Shield,
            title: 'RC Professionale Medica',
            subtitle: 'Colpa Medica & Colpa Grave',
            description: 'Coperture ad alto massimale in conformità con la Legge Gelli-Bianco per chirurghi, medici ospedalieri, MMG e liberi professionisti.',
            partnerText: 'AmTrust Medici & Lloyd\'s of London',
        },
        {
            icon: Scale,
            title: 'Tutela Legale Sanitaria',
            subtitle: 'Difesa Penale & Civile',
            description: 'Copertura specifica per spese legali, periti di parte e procedimenti giudiziari legati all\'esercizio dell\'attività medica.',
            partnerText: 'Tutela Legale SPA',
        },
        {
            icon: Heart,
            title: 'Vita & Infortuni',
            subtitle: 'Protezione Persona & Capacità Reddituale',
            description: 'Tutela da invalidità permanente, diarie da ricovero e coperture vita a garanzia della sicurezza della tua famiglia.',
            partnerText: 'Generali & MetLife',
        },
        {
            icon: HomeIcon,
            title: 'Patrimonio & Studio',
            subtitle: 'Protezione Capofamiglia & RCT Studio',
            description: 'Protezione integrata dei beni personali e garanzie di responsabilità civile per titolari di studio e poliambulatorio.',
            partnerText: 'Itas Mutua & Reale Mutua',
        },
    ];

    const stats = [
        { number: '30 Anni', label: 'Esperienza per la Classe Medica', icon: Award },
        { number: '1.000+', label: 'Medici & Odontoiatri Assistiti', icon: Users },
        { number: '2.000+', label: 'Polizze Attive in Italia', icon: ShieldCheck },
    ];

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans antialiased selection:bg-[#0066FF] selection:text-white">

            {/* 1. STICKY HEADER WITH LOGO & MENU */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm transition-all duration-300">
                <div className="container mx-auto px-4 py-3.5 flex items-center justify-between">
                    <a href="#" className="flex items-center gap-3">
                        <Logo size="sm" />
                        <div className="hidden sm:block pl-3 border-l border-slate-200">
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#0066FF] block">
                                Mauro Astore
                            </span>
                            <span className="text-[11px] text-slate-500 block">
                                Broker Assicurativo • Wide Group
                            </span>
                        </div>
                    </a>

                    <nav className="hidden lg:flex items-center gap-8">
                        <a href="#specializzazioni" className="text-slate-700 hover:text-[#0066FF] font-medium text-sm transition-colors">
                            Configuratore Rischio
                        </a>
                        <a href="#chi-sono" className="text-slate-700 hover:text-[#0066FF] font-medium text-sm transition-colors">
                            Mauro Astore
                        </a>
                        <a href="#pilastri" className="text-slate-700 hover:text-[#0066FF] font-medium text-sm transition-colors">
                            Garanzie
                        </a>
                        <a href="#partner" className="text-slate-700 hover:text-[#0066FF] font-medium text-sm transition-colors">
                            Partner
                        </a>
                        <a href="#contatti" className="text-slate-700 hover:text-[#0066FF] font-medium text-sm transition-colors">
                            Contatti
                        </a>
                    </nav>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
                            className="bg-[#0066FF] hover:bg-[#0052cc] text-white text-xs sm:text-sm font-bold py-2.5 px-4 sm:px-5 rounded-xl transition-all duration-300 flex items-center gap-2 shadow-sm"
                        >
                            <span>Analisi dei Rischi</span>
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </header>

            {/* 2. HERO SECTION WITH PRINCIPAL CTA */}
            <section className="relative bg-white text-slate-900 py-16 sm:py-24 overflow-hidden border-b border-slate-200">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        {/* Regulatory Badge */}
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-[#0066FF]/30 text-[#0066FF] text-xs font-bold uppercase tracking-wider mb-6">
                            <ShieldCheck size={14} />
                            Iscrizione RUI N° E000079870 • Circuito Wide Group
                        </div>

                        {/* Main Headline */}
                        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-[#0B192C] mb-6 leading-tight tracking-tight">
                            Protezione Assicurativa per la <span className="text-[#0066FF]">Classe Medica</span>
                        </h1>

                        {/* Subtitle */}
                        <p className="text-lg sm:text-2xl text-slate-600 mb-10 leading-relaxed font-normal max-w-3xl mx-auto">
                            Consulenza specialistica indipendente per la valutazione dei rischi di chirurghi, medici ospedalieri, MMG e liberi professionisti.
                        </p>

                        {/* Hero CTAs */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
                            <button
                                onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
                                className="w-full sm:w-auto bg-[#0066FF] hover:bg-[#0052cc] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 text-base sm:text-lg flex items-center justify-center gap-3 shadow-md hover:scale-[1.02]"
                            >
                                <span>Richiedi Analisi dei Rischi</span>
                                <ArrowRight size={20} />
                            </button>
                            <button
                                onClick={() => document.getElementById('specializzazioni')?.scrollIntoView({ behavior: 'smooth' })}
                                className="w-full sm:w-auto bg-white hover:bg-slate-50 text-[#0B192C] border border-slate-300 font-bold py-4 px-8 rounded-xl transition-all duration-300 text-base sm:text-lg flex items-center justify-center gap-2 shadow-sm"
                            >
                                <span>Configura per Specializzazione</span>
                            </button>
                        </div>

                        {/* Trust Badges Bar */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto pt-8 border-t border-slate-200">
                            <div className="flex items-center justify-center gap-2 text-slate-700 text-xs sm:text-sm font-semibold">
                                <CheckCircle2 size={16} className="text-[#0066FF]" />
                                <span>Legge Gelli-Bianco</span>
                            </div>
                            <div className="flex items-center justify-center gap-2 text-slate-700 text-xs sm:text-sm font-semibold">
                                <CheckCircle2 size={16} className="text-[#0066FF]" />
                                <span>Retroattività Illimitata</span>
                            </div>
                            <div className="flex items-center justify-center gap-2 text-slate-700 text-xs sm:text-sm font-semibold">
                                <CheckCircle2 size={16} className="text-[#0066FF]" />
                                <span>Tutela Legale Penale</span>
                            </div>
                            <div className="flex items-center justify-center gap-2 text-slate-700 text-xs sm:text-sm font-semibold">
                                <CheckCircle2 size={16} className="text-[#0066FF]" />
                                <span>Partner Wide Group</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. IMPACT STATISTICS */}
            <section className="py-12 bg-white border-b border-slate-200 text-slate-900">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {stats.map((stat, idx) => {
                            const Icon = stat.icon;
                            return (
                                <div
                                    key={idx}
                                    className="flex items-center gap-5 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <div className="p-4 rounded-xl bg-blue-50 text-[#0066FF]">
                                        <Icon size={36} />
                                    </div>
                                    <div>
                                        <div className="text-3xl font-extrabold text-[#0B192C] tracking-tight">
                                            {stat.number}
                                        </div>
                                        <div className="text-slate-600 text-sm font-medium mt-1">
                                            {stat.label}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 4. SPECIALIZATION FILTER (CONFIGURATORE RAPIDO) */}
            <div id="specializzazioni">
                <SpecializationFilter />
            </div>

            {/* 5. BIO MAURO ASTORE (FIRST PERSON WITH SAILING METAPHOR & PHOTO) */}
            <section id="chi-sono" className="py-20 bg-white text-slate-900 relative overflow-hidden border-b border-slate-200">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">

                            {/* Photo Side */}
                            <div className="w-full lg:w-5/12 flex-shrink-0">
                                <div className="relative group max-w-md mx-auto lg:max-w-none">
                                    <img
                                        src="/foto-mauro.jpg"
                                        onError={(e) => {
                                            e.currentTarget.src = '/fotoMauro.jpeg';
                                        }}
                                        alt="Mauro Astore - Broker Assicurativo"
                                        className="relative w-full h-auto rounded-2xl shadow-xl object-cover border-2 border-slate-200"
                                    />
                                </div>
                            </div>

                            {/* Bio Narrative Side */}
                            <div className="w-full lg:w-7/12">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-[#0066FF]/30 text-[#0066FF] text-xs font-bold uppercase tracking-wider mb-4">
                                    <Compass size={14} />
                                    Consulenza Specialistica Indipendente
                                </div>

                                <h2 className="text-3xl sm:text-5xl font-extrabold text-[#0B192C] mb-2">
                                    Mauro Astore
                                </h2>
                                <p className="text-lg sm:text-xl text-[#0066FF] font-semibold mb-6">
                                    Broker Assicurativo | Iscrizione RUI N° E000079870 | Circuito Wide Group
                                </p>

                                <div className="space-y-5 text-slate-700 leading-relaxed text-base sm:text-lg font-normal">
                                    <p>
                                        Sono <strong>Mauro Astore</strong>. Da <strong>oltre 30 anni</strong> dedico la mia vita professionale alla difesa e alla protezione della classe medica. Ho seguito in prima persona l'evoluzione della responsabilità sanitaria in Italia, dai primi sviluppi giurisprudenziali fino alla definizione della <strong>Legge Gelli-Bianco</strong>, maturando una profonda conoscenza tecnica e operativa delle criticità a cui ogni medico è quotidianamente esposto.
                                    </p>

                                    <p>
                                        Nel corso dei miei 30 anni di attività sul campo, ho assistito oltre <strong>1.000 medici e professionisti sanitari</strong> in tutta Italia, gestendo oltre <strong>2.000 polizze attive</strong>. Non si tratta di semplici numeri, ma di relazioni di fiducia durature nate dalla vicinanza concreta a chirurghi, medici di medicina generale, pediatri di libera scelta, odontoiatri e giovani specializzandi.
                                    </p>

                                    <p>
                                        Nel settore assicurativo professionale la precisione è tutto. <strong>La mia passione per la vela mi ha insegnato l'importanza di analizzare attentamente i venti, anticipare le correnti e mantenere sempre salda la rotta, specialmente quando il mare è agitato.</strong> Applico esattamente lo stesso approccio nel mio lavoro: analizzo con estremo rigore il profilo di rischio di ogni medico per costruire una copertura solida e inattaccabile, permettendogli di svolgere la propria attività con totale serenità.
                                    </p>

                                    <p>
                                        In qualità di broker del circuito <strong>Wide Group</strong>, opero in piena indipendenza sul mercato nazionale ed internazionale, selezionando le migliori soluzioni tra i primari marchi del settore — come <strong>AmTrust Medici, Lloyd's of London, Tutela Legale SPA, Generali e MetLife</strong>.
                                    </p>
                                </div>

                                <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-6 text-xs sm:text-sm text-slate-600">
                                    <div>
                                        <span className="block text-[#0B192C] font-bold text-sm">Iscrizione RUI</span>
                                        <span>N° E000079870 (Assimood Broker)</span>
                                    </div>
                                    <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
                                    <div>
                                        <span className="block text-[#0B192C] font-bold text-sm">Qualifica Professionale</span>
                                        <span>Partner del circuito Wide Group</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* 6. I 4 PILASTRI DI PROTEZIONE */}
            <section id="pilastri" className="py-20 bg-white border-b border-slate-200">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0066FF] text-xs font-bold uppercase tracking-wider mb-3 border border-[#0066FF]/20">
                            <Shield size={14} />
                            Struttura delle Coperture
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B192C] mb-4">
                            I 4 Pilastri della Protezione Medica
                        </h2>
                        <p className="text-slate-600 text-base sm:text-lg">
                            Un'architettura di garanzie modulare e completa per difendere la tua operatività clinica, il tuo patrimonio e la continuità del tuo studio.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {pillars.map((pillar, idx) => {
                            const Icon = pillar.icon;
                            return (
                                <div
                                    key={idx}
                                    className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
                                >
                                    <div>
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="p-4 rounded-xl bg-blue-50 text-[#0066FF] group-hover:bg-[#0066FF] group-hover:text-white transition-colors duration-300">
                                                <Icon size={32} />
                                            </div>
                                            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
                                                {pillar.subtitle}
                                            </span>
                                        </div>

                                        <h3 className="text-2xl font-bold text-[#0B192C] mb-3">
                                            {pillar.title}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed text-base mb-6">
                                            {pillar.description}
                                        </p>
                                    </div>

                                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                                        <span className="text-slate-500 font-medium">Partner di riferimento:</span>
                                        <span className="font-bold text-[#0B192C]">{pillar.partnerText}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 7. PARTNER DI ECCELLENZA */}
            <section id="partner" className="py-16 bg-white border-b border-slate-200">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B192C] mb-2">
                            Partner di Eccellenza Assicurativa
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base">
                            Selezioniamo i primari mercati nazionali ed internazionali per la gestione indipendente del rischio.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 max-w-6xl mx-auto">
                        {partners.map((partner, index) => (
                            <div
                                key={index}
                                className="bg-white p-4 rounded-xl border border-slate-200 text-center flex flex-col justify-center items-center hover:border-[#0066FF] hover:shadow-md transition-all duration-300"
                            >
                                <span className="font-bold text-[#0B192C] text-xs sm:text-sm">
                                    {partner.name}
                                </span>
                                <span className="text-[10px] text-slate-500 mt-1">
                                    {partner.category}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 8. MODULO DI CONTATTO RAPIDO */}
            <section id="contatti" className="py-20 bg-white text-slate-900 border-b border-slate-200 relative">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B192C] mb-4">
                                Richiedi un'Analisi Personalizzata dei Rischi
                            </h2>
                            <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
                                Compila il form sottostante per verificare la rispondenza delle tue coperture attuali alle normative vigenti. Risposta garantita da Mauro Astore entro 24 ore.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                            {/* Left Column: Direct Contacts */}
                            <div className="lg:col-span-2 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                                <h3 className="text-xl font-bold text-[#0B192C] border-b border-slate-200 pb-3">
                                    Recapiti Diretti
                                </h3>

                                <div className="space-y-4 text-sm">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2.5 rounded-lg bg-blue-50 text-[#0066FF] shrink-0">
                                            <Phone size={20} />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800">Telefono / Direct Line</p>
                                            <a href="tel:+393287515084" className="text-[#0066FF] hover:underline font-bold">
                                                +39 328 751 5084
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="p-2.5 rounded-lg bg-blue-50 text-[#0066FF] shrink-0">
                                            <Mail size={20} />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800">Email Consulenza</p>
                                            <a href="mailto:astoremauro@gmail.com" className="text-[#0066FF] hover:underline block font-medium">
                                                astoremauro@gmail.com
                                            </a>
                                            <a href="mailto:astoremauro1965@gmail.com" className="text-slate-500 text-xs hover:underline block mt-0.5">
                                                astoremauro1965@gmail.com
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="p-2.5 rounded-lg bg-blue-50 text-[#0066FF] shrink-0">
                                            <MapPin size={20} />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800">Operatività</p>
                                            <p className="text-slate-600 text-xs">
                                                Consulenza su scala nazionale in tutta Italia (In presenza / Remoto)
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-200">
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1">
                                        <span className="font-bold text-[#0B192C] block">Iscrizione Registro Unico Intermediari:</span>
                                        <p>Mauro Astore • RUI N° E000079870</p>
                                        <p>Assimood Broker • Circuito Wide Group</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Contact Form */}
                            <div className="lg:col-span-3 bg-white text-slate-900 p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-md">
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                                Nome e Cognome *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 text-sm font-medium bg-white"
                                                placeholder="Dr. Mario Rossi"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                                Email Professionale *
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 text-sm font-medium bg-white"
                                                placeholder="mario.rossi@ordineinail.it"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                                Recapito Telefonico
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 text-sm font-medium bg-white"
                                                placeholder="+39 333 1234567"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                                Specializzazione / Ruolo
                                            </label>
                                            <select
                                                value={formData.specialization}
                                                onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 text-sm font-medium bg-white"
                                            >
                                                <option value="">Seleziona area medica...</option>
                                                <option value="chirurgia">Chirurgia & Area Critica</option>
                                                <option value="mmg">Medicina Generale / PLS</option>
                                                <option value="specializzando">Specializzando / Neo-Abilitato</option>
                                                <option value="odontoiatria">Odontoiatria / Studio Medico</option>
                                                <option value="altro">Altra Disciplina Medica</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                                            Messaggio o Richiesta Specifica *
                                        </label>
                                        <textarea
                                            required
                                            rows={4}
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 text-sm font-medium bg-white"
                                            placeholder="Descrivi brevemente le tue attuali coperture o i massimali desiderati..."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={formSubmitted}
                                        className="w-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:scale-[1.01]"
                                    >
                                        <Send size={18} />
                                        <span>Invia Richiesta di Analisi Rischio</span>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 9. FOOTER & COMPLIANCE IVASS */}
            <footer className="bg-white text-slate-700 border-t border-slate-200 pt-12 pb-8">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">

                        {/* Col 1: Broker Info */}
                        <div className="md:col-span-2 space-y-4">
                            <Logo size="sm" />
                            <p className="text-slate-600 text-sm leading-relaxed max-w-lg">
                                Consulenza e intermediazione assicurativa ad alto profilo istituzionale riservata alla classe medica e alle strutture sanitarie.
                            </p>
                            <div className="text-xs text-slate-500 space-y-1 pt-2 border-t border-slate-200">
                                <p className="font-semibold text-[#0B192C]">
                                    Mauro Astore - Broker Assicurativo | Assimood Broker
                                </p>
                                <p>Iscrizione RUI N° E000079870 | P.IVA 01342630561</p>
                                <p className="text-[#0066FF] font-semibold">Partner del circuito Wide Group</p>
                            </div>
                        </div>

                        {/* Col 2: Navigation Links */}
                        <div>
                            <h4 className="font-bold text-[#0B192C] text-sm uppercase tracking-wider mb-4">
                                Navigazione Piattaforma
                            </h4>
                            <ul className="space-y-2.5 text-sm text-slate-600">
                                <li><a href="#specializzazioni" className="hover:text-[#0066FF] transition-colors">Configuratore Specializzazioni</a></li>
                                <li><a href="#chi-sono" className="hover:text-[#0066FF] transition-colors">Mauro Astore</a></li>
                                <li><a href="#pilastri" className="hover:text-[#0066FF] transition-colors">I 4 Pilastri di Protezione</a></li>
                                <li><a href="#partner" className="hover:text-[#0066FF] transition-colors">Partner</a></li>
                                <li><a href="#contatti" className="hover:text-[#0066FF] transition-colors">Richiedi Analisi Rischi</a></li>
                            </ul>
                        </div>

                        {/* Col 3: Legal & Regulatory */}
                        <div>
                            <h4 className="font-bold text-[#0B192C] text-sm uppercase tracking-wider mb-4">
                                Conformità IVASS & Note
                            </h4>
                            <ul className="space-y-2.5 text-sm text-slate-600">
                                <li><a href="#" className="hover:text-[#0066FF] transition-colors">Informativa Precontrattuale IVASS</a></li>
                                <li><a href="#" className="hover:text-[#0066FF] transition-colors">Privacy Policy & GDPR Sanitario</a></li>
                                <li><a href="#" className="hover:text-[#0066FF] transition-colors">Cookie Policy</a></li>
                                <li><a href="#" className="hover:text-[#0066FF] transition-colors">Reclami & Regolamento RUI</a></li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Compliance Line */}
                    <div className="border-t border-slate-200 pt-6 text-center text-xs text-slate-500">
                        <p className="leading-relaxed">
                            Mauro Astore - Broker Assicurativo | Assimood Broker | Iscrizione RUI N° E000079870 | P.IVA 01342630561 | Partner del circuito Wide Group
                        </p>
                        <p className="mt-1 text-slate-400">
                            © {new Date().getFullYear()} Tutti i diritti riservati. Attività soggetta al controllo IVASS.
                        </p>
                    </div>
                </div>
            </footer>

            {/* 10. GLOBAL FLOATING WHATSAPP BUTTON */}
            <WhatsAppButton />
        </div>
    );
};

export default Home;
